// WHEP player (init-handoff §4.5). recvonly RTCPeerConnection; POST SDP offer to the WHEP URL;
// set the remote answer. HTTPS (or localhost) required for WebRTC.
import type { PlaybackEndpoint } from "../api/types.js";
import type { PlayerDriver } from "./types.js";

export class WhepPlayer implements PlayerDriver {
  private pc: RTCPeerConnection | null = null;

  async attach(video: HTMLVideoElement, ep: PlaybackEndpoint): Promise<void> {
    const pc = new RTCPeerConnection({ iceServers: [{ urls: "stun:stun.l.google.com:19302" }] });
    this.pc = pc;
    pc.addTransceiver("video", { direction: "recvonly" });
    pc.addTransceiver("audio", { direction: "recvonly" });

    const stream = new MediaStream();
    pc.ontrack = (e) => {
      stream.addTrack(e.track);
      video.srcObject = stream;
    };

    const offer = await pc.createOffer();
    await pc.setLocalDescription(offer);
    await waitIceGathering(pc, 2000);

    const res = await fetch(ep.url, {
      method: "POST",
      headers: { "Content-Type": "application/sdp" },
      body: pc.localDescription?.sdp ?? offer.sdp ?? "",
    });
    if (!res.ok) throw new Error(`WHEP signaling failed: ${res.status}`);
    const answer = await res.text();
    await pc.setRemoteDescription({ type: "answer", sdp: answer });

    await new Promise<void>((resolve, reject) => {
      const timer = setTimeout(() => reject(new Error("WHEP connect timeout")), 8000);
      pc.onconnectionstatechange = () => {
        if (pc.connectionState === "connected") {
          clearTimeout(timer);
          resolve();
        } else if (pc.connectionState === "failed") {
          clearTimeout(timer);
          reject(new Error("WHEP connection failed"));
        }
      };
    });
  }

  destroy(): void {
    this.pc?.getReceivers().forEach((r) => r.track?.stop());
    this.pc?.close();
    this.pc = null;
  }
}

function waitIceGathering(pc: RTCPeerConnection, timeoutMs: number): Promise<void> {
  if (pc.iceGatheringState === "complete") return Promise.resolve();
  return new Promise((resolve) => {
    const done = () => {
      pc.removeEventListener("icegatheringstatechange", check);
      resolve();
    };
    const check = () => {
      if (pc.iceGatheringState === "complete") done();
    };
    pc.addEventListener("icegatheringstatechange", check);
    setTimeout(done, timeoutMs); // trickle fallback: send what we have
  });
}
